import { useEffect, useState } from "react"

import { App, Empty, Flex, Modal, Spin, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import Icon from "../../../../../Icon"
import { resetDetails, selectDetails } from "../../redux/requestsReceipts.slice"
import {
  exportReceiptProfileDetails,
  getReceiptRequestDetails
} from "../../redux/requestsReceipts.thunks"

import styles from "./styles.module.less"

const { Text, Link } = Typography

const EMPTY = "—"

/**
 * ProfileDetailsModal — Eye icon → read-only snapshot receipt content (§6.1).
 *
 * Показує дані у 4 секціях:
 *   - Top (Program, Business account, User, Website)
 *   - Step 1 · Business info (3)
 *   - Step 2 · Business details (9)
 *   - Step 4 · Support & legal (6)
 *
 * Data flow: при відкритті модалки dispatch-ає `getReceiptRequestDetails(id)`;
 * поки грузить — spinner; error → Empty. Кожне value з copy icon.
 */
const ProfileDetailsModal = ({ open, id, onClose }) => {
  const dispatch = useDispatch()
  const { notification } = App.useApp()
  const { fetching, data, error } = useSelector(selectDetails)
  const [exportFetching, setExportFetching] = useState(false)

  const handleExport = async () => {
    setExportFetching(true)
    try {
      await dispatch(exportReceiptProfileDetails(id)).unwrap()
      notification.success({
        message: "Profile details downloaded",
        closeIcon: <Text>Dismiss</Text>,
        style: { minWidth: "max-content", width: 475 }
      })
    } catch {
      // Global error notification already shown from thunk (handleApiError).
    } finally {
      setExportFetching(false)
    }
  }

  useEffect(() => {
    if (open && id != null) {
      dispatch(getReceiptRequestDetails(id))
    }
    if (!open) {
      dispatch(resetDetails())
    }
  }, [open, id, dispatch])

  const record = data || {}
  const profile = record.receipt_profile || {}
  const businessAccountName =
    record?.business_account?.business_account || record?.business_account?.name

  const renderValue = (value, { isLink } = {}) => {
    if (!value) return <Text type="secondary">{EMPTY}</Text>
    if (isLink) {
      return (
        <Flex gap={8} align="center">
          <Link href={value} target="_blank" onClick={(e) => e.stopPropagation()}>
            {value}
          </Link>
          <Text copyable={{ text: value, tooltips: false }} />
        </Flex>
      )
    }
    return (
      <Flex gap={8} align="center">
        <Text>{value}</Text>
        <Text copyable={{ text: String(value), tooltips: false }} />
      </Flex>
    )
  }

  const renderLogoValue = (logo) => {
    if (!logo || !logo.file_name) return <Text type="secondary">{EMPTY}</Text>
    return (
      <Flex gap={8} align="center">
        <div className={styles.logoThumb} aria-hidden>
          {logo.file_url ? (
            <img src={logo.file_url} alt="" />
          ) : (
            <Icon type="image" style={{ opacity: 0.5 }} />
          )}
        </div>
        <Text>{logo.file_name}</Text>
        <Text copyable={{ text: logo.file_name, tooltips: false }} />
      </Flex>
    )
  }

  const rows = [
    {
      field: "Program",
      testId: "program",
      value: renderValue(profile.program || record.enrollments)
    },
    {
      field: "Business account",
      testId: "business_account",
      value: renderValue(businessAccountName)
    },
    {
      field: "User full name",
      testId: "user_full_name",
      value: renderValue(profile.user_full_name || record.user_full_name)
    },
    {
      field: "User email",
      testId: "user_email",
      value: renderValue(profile.user_email || record.user_email)
    },
    {
      field: "Website",
      testId: "website",
      value: renderValue(profile.website || record.business_url, { isLink: true })
    },

    { section: "STEP 1 · BUSINESS INFO (3)" },
    { field: "Business name", testId: "business_name", value: renderValue(profile.business_name) },
    {
      field: "Business website",
      testId: "business_website",
      value: renderValue(profile.business_website, { isLink: true })
    },
    {
      field: "Business description",
      testId: "business_description",
      value: renderValue(profile.business_description)
    },

    { section: "STEP 2 · BUSINESS DETAILS (9)" },
    {
      field: "Business logo",
      testId: "business_logo",
      value: renderLogoValue(profile.business_logo)
    },
    {
      field: "Business address",
      testId: "business_address",
      value: renderValue(profile.business_address)
    },
    { field: "City", testId: "city", value: renderValue(profile.city) },
    { field: "Postal code", testId: "postal_code", value: renderValue(profile.postal_code) },
    { field: "Country", testId: "country", value: renderValue(profile.country) },
    { field: "Country code", testId: "country_code", value: renderValue(profile.country_code) },
    { field: "State/Region", testId: "state_region", value: renderValue(profile.state_region) },
    {
      field: "Product/service name",
      testId: "product_name",
      value: renderValue(profile.product_name)
    },
    {
      field: "Product/service description",
      testId: "product_description",
      value: renderValue(profile.product_description)
    },

    { section: "STEP 4 · SUPPORT & LEGAL (6)" },
    { field: "Support phone", testId: "support_phone", value: renderValue(profile.support_phone) },
    { field: "Support email", testId: "support_email", value: renderValue(profile.support_email) },
    {
      field: "Support website",
      testId: "support_website",
      value: renderValue(profile.support_website, { isLink: true })
    },
    {
      field: "Terms & conditions",
      testId: "terms_url",
      value: renderValue(profile.terms_url, { isLink: true })
    },
    {
      field: "Refund policy",
      testId: "refund_policy_url",
      value: renderValue(profile.refund_policy_url, { isLink: true })
    },
    {
      field: "Cancellation policy",
      testId: "cancellation_policy_url",
      value: renderValue(profile.cancellation_policy_url, { isLink: true })
    }
  ]

  const modalTitle = (
    <Flex align="center" justify="space-between" style={{ paddingRight: 32 }}>
      <span>Profile details</span>
      <ButtonComponent
        icon={<Icon type="file_download" />}
        loading={exportFetching}
        disabled={!data}
        onClick={handleExport}
      >
        Download
      </ButtonComponent>
    </Flex>
  )

  return (
    <Modal
      open={open}
      title={modalTitle}
      onCancel={onClose}
      footer={null}
      width={720}
      centered
      destroyOnHidden
      data-testid="deflect-admin-profile-details-modal"
    >
      {fetching && !data ? (
        <Flex align="center" justify="center" style={{ minHeight: 320 }}>
          <Spin size="large" />
        </Flex>
      ) : error ? (
        <Flex align="center" justify="center" style={{ minHeight: 320 }}>
          <Empty description="Failed to load profile details." />
        </Flex>
      ) : (
        <div className={styles.table}>
          <div className={styles.headerRow}>
            <div className={styles.field}>FIELD</div>
            <div className={styles.value}>VALUE</div>
          </div>
          {rows.map((row, i) =>
            row.section ? (
              <div key={`s-${i}`} className={styles.sectionHeader}>
                {row.section}
              </div>
            ) : (
              <div
                key={row.field}
                className={styles.row}
                data-testid={`deflect-admin-profile-details-row-${row.testId}`}
              >
                <div className={styles.field}>{row.field}</div>
                <div className={styles.value}>{row.value}</div>
              </div>
            )
          )}
        </div>
      )}
    </Modal>
  )
}

export default ProfileDetailsModal
